import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/context/AuthContext";
import { useCrumbs } from "@/context/CrumbContext";
import { ArrowLeft, BarChart3, Sparkles, TrendingUp } from "lucide-react";

export default function Analytics() {
  const navigate = useNavigate();
  useCrumbs([{ label: "AI Analiz", to: "/analytics" }]);
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(8);

  useEffect(() => { (async () => {
    try {
      const { data } = await apiClient.get("/analytics/ai-accuracy", { params: { weeks } });
      setData(data);
    } catch { /* ignore */ }
  })(); }, [weeks]);

  const maxCount = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.weeks.map((w) => Math.max(w.accepted + w.rejected + w.pending, 1)));
  }, [data]);

  return (
    <div className="detail-shell" data-testid="analytics-page">
      <header className="detail-topbar">
        <button className="icon-button" onClick={() => navigate("/home")} data-testid="analytics-back"><ArrowLeft size={17}/></button>
        <div className="detail-head">
          <div className="eyebrow">AI DOĞRULUK DASHBOARD</div>
          <h1>Kroki önerileri · haftalık kabul oranı</h1>
          <small><Sparkles size={13}/> Gemini 3.1 Pro model performansı</small>
        </div>
        <div className="detail-actions">
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} data-testid="analytics-weeks" className="chip" style={{padding:"6px 10px"}}>
            <option value="4">Son 4 hafta</option>
            <option value="8">Son 8 hafta</option>
            <option value="12">Son 12 hafta</option>
            <option value="26">Son 26 hafta</option>
          </select>
        </div>
      </header>
      <div className="detail-body">
        {!data ? (
          <small>Yükleniyor…</small>
        ) : (
          <>
            <section className="detail-grid" data-testid="analytics-overview">
              <div className="section-block">
                <div className="section-title"><div><div className="eyebrow">GENEL</div><h2>Doğruluk özeti</h2></div></div>
                <div className="overview-metrics">
                  <div><strong>{data.overall.total_verified}</strong><small>Doğrulanmış öneri</small></div>
                  <div><strong>{data.overall.accepted}</strong><small>Kabul edilen</small></div>
                  <div className={(data.overall.acceptance_rate ?? 0) >= 50 ? "" : "warn"}>
                    <strong>{data.overall.acceptance_rate ?? "—"}%</strong><small>Kabul oranı</small>
                  </div>
                  <div><strong>{data.overall.avg_confidence}%</strong><small>Ortalama güven</small></div>
                </div>
              </div>
              <div className="section-block">
                <div className="section-title"><div><div className="eyebrow">TİPE GÖRE</div><h2>Öneri sınıfları</h2></div></div>
                <div className="type-rows" data-testid="analytics-by-type">
                  {data.by_type.length === 0 && <small className="evidence-empty">Henüz yeterli veri yok. AI kroki analizini çalıştırın.</small>}
                  {data.by_type.map((t) => (
                    <div key={t.type} className="type-row" data-testid={`analytics-type-${t.type}`}>
                      <strong>{t.type}</strong>
                      <small>{t.accepted}/{t.total} kabul · güven %{t.avg_confidence}</small>
                      <div className="mini-bar"><i style={{width: `${t.acceptance_rate || 0}%`, background: (t.acceptance_rate || 0) >= 60 ? "#5ee6b3" : "#f8bd56"}}/></div>
                      <span className="rule-score">{t.acceptance_rate ?? "—"}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            <section className="section-block">
              <div className="section-title"><div><div className="eyebrow">HAFTA HAFTA</div><h2><TrendingUp size={16}/> Son {weeks} hafta</h2></div></div>
              <div className="chart" data-testid="analytics-chart" style={{display:"grid", gridTemplateColumns:`repeat(${data.weeks.length},1fr)`, gap:4, alignItems:"end", height:220, padding:"20px 0", borderBottom:"1px solid var(--line)"}}>
                {data.weeks.map((w, i) => {
                  const total = w.accepted + w.rejected + w.pending;
                  const h = (total / maxCount) * 200;
                  const okH = total ? (w.accepted / total) * h : 0;
                  const badH = total ? (w.rejected / total) * h : 0;
                  const pH = total ? (w.pending / total) * h : 0;
                  return (
                    <div key={i} className="chart-bar" data-testid={`chart-week-${i}`} title={`${w.week_start}: ${w.accepted}/${w.rejected}/${w.pending}`} style={{display:"flex", flexDirection:"column", justifyContent:"flex-end", alignItems:"center", gap:1}}>
                      <div style={{width:"80%", background:"#f8bd56", height:pH}}/>
                      <div style={{width:"80%", background:"#fa6651", height:badH}}/>
                      <div style={{width:"80%", background:"#5ee6b3", height:okH}}/>
                      <small style={{color:"#64748b", fontSize:9, fontFamily:"DM Mono"}}>{(w.week_start||"").slice(5)}</small>
                    </div>
                  );
                })}
              </div>
              <div className="map-legend" style={{marginTop:12}}>
                <span className="chip" style={{color:"#5ee6b3", borderColor:"#5ee6b3"}}>● Kabul</span>
                <span className="chip" style={{color:"#fa6651", borderColor:"#fa6651"}}>● Red</span>
                <span className="chip" style={{color:"#f8bd56", borderColor:"#f8bd56"}}>● Bekleyen</span>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
